<script lang="ts">
  /**
   * Intake — the screen that decides whether Vault gets used.
   *
   * The old version was one file input and one form: pick, type six fields, press
   * File, wait for the upload. Getting a drawer of paper in meant repeating that
   * dozens of times, so nobody would, and the spec is blunt about where that ends —
   * "an empty store teaches people the tool does not work".
   *
   * What changed:
   *
   *  · **Five ways in.** Drop, choose, paste, phone camera, Google Drive. The drop
   *    zone covers the whole screen, because a dragged file lands where the hand
   *    happens to be, not on a 200px rectangle.
   *  · **Uploading overlaps typing.** Bytes start moving the instant a file arrives;
   *    by the time the details are filled in, filing is a metadata write.
   *  · **Many at once**, each row filed independently, with a *File all* for the
   *    common case where the guesses are already right.
   *  · **Guesses are labelled as guesses.** A title inferred from a filename is shown
   *    with a "from the filename" mark. An inferred field that looks typed gets filed
   *    unread; one that admits where it came from gets corrected.
   *
   * What deliberately did not change: nothing files itself. Every row still needs a
   * press, per the spec's "no item leaves the queue without a human press".
   */
  import { Icon } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  /**
   * Mirrors RENEWABLE_TYPES on the server. Duplicated deliberately rather than
   * fetched: it only decides which way an unticked box *renders*, and the server
   * re-derives the real answer from the same list, so a drift between the two
   * shows a wrong tick and never files a wrong value.
   */
  const RENEWABLE_DEFAULT = new Set(["vehicle", "identity", "insurance", "warranty", "contract", "medical"]);
  import * as drive from "../lib/drive";
  import { infer, nextKey, size, transferAll, type QueueItem } from "../lib/intake";
  import type { Check } from "../lib/model";

  type Dup = { id: string; title: string; why: string; documentDate: string | null; amount: number | null };
  type Meta = { docTypes: string[]; expiryKinds: string[]; suggested: Record<string, string> };

  const {
    meta,
    onFiled,
    pretty,
  }: {
    meta: Meta | null;
    /** Told after each successful file, so the Expiring panel can refresh. */
    onFiled: () => void;
    pretty: (s: string) => string;
  } = $props();

  /** Per-row detail, keyed by queue key. Kept beside the queue rather than inside it
   *  so intake.ts stays free of form concerns. */
  type Detail = {
    title: string;
    docType: string;
    supplier: string;
    policyNumber: string;
    amount: string;
    documentDate: string;
    expiresAt: string;
    expiryKind: string;
    inCase: boolean;
    /** Null = use the document type's default. */
    notifyOnExpiry: boolean | null;
    notes: string;
    dups: Dup[];
    /** Which fields the filename supplied, so the row can say so. */
    guessed: string[];
    checks: Check[];
    filedId: string | null;
  };

  let queue = $state<QueueItem[]>([]);
  let details = $state<Record<string, Detail>>({});
  let dragging = $state(false);
  let note = $state<{ kind: "ok" | "warn"; text: string } | null>(null);
  let filingAll = $state(false);

  // ── Drive ────────────────────────────────────────────────────────────────
  let driveOpen = $state(false);
  let driveBusy = $state(false);
  let driveErr = $state<string | null>(null);
  let driveFiles = $state<drive.DriveFile[]>([]);
  let drivePicked = $state<Record<string, boolean>>({});
  let driveConnected = $state(false);

  const pending = $derived(queue.filter((i) => i.status !== "filed"));
  const readyCount = $derived(queue.filter((i) => i.status === "ready").length);

  /** Svelte 5 tracks the array, not fields mutated inside intake.ts. Reassigning the
   *  reference is what makes progress render — cheaper than proxying every item. */
  function touch(): void {
    queue = [...queue];
  }

  function add(files: Array<{ blob: Blob; filename: string; mime: string }>, origin: QueueItem["origin"]): void {
    if (files.length === 0) return;

    const fresh: QueueItem[] = files.map((f) => {
      const guess = infer(f.filename);
      const key = nextKey();
      details[key] = {
        title: guess.title,
        docType: guess.docType,
        supplier: "",
        policyNumber: "",
        amount: "",
        documentDate: guess.documentDate,
        expiresAt: "",
        expiryKind: "",
        inCase: false,
        notifyOnExpiry: null,
        notes: "",
        dups: [],
        guessed: guess.from,
        checks: [],
        filedId: null,
      };
      return {
        key,
        filename: f.filename,
        mime: f.mime || "application/octet-stream",
        sizeBytes: f.blob.size,
        blob: f.blob,
        origin,
        status: "queued",
        progress: 0,
        gcsPath: null,
        sha256: null,
        error: null,
        guess,
      };
    });

    queue = [...queue, ...fresh];

    /**
     * Transfer the items **as they now live in `queue`**, not the plain objects
     * built above.
     *
     * `$state` deep-proxies whatever is assigned into it, so `queue` holds
     * proxies while `fresh` still holds the raw originals. Handing the originals
     * to `transferAll` meant every `item.status` and `item.progress` write went
     * to an object Svelte was not tracking: the row rendered "Reading…" once and
     * never changed again, no matter what the transfer actually did. The stage
     * timeouts fired correctly and had nowhere visible to land.
     */
    const tracked = queue.slice(-fresh.length);
    // Not awaited: the person should be typing into row one while row eight uploads.
    void transferAll(tracked, touch).then(() => {
      for (const i of tracked) void lookForDuplicates(i);
    });
  }

  function fromFileList(list: FileList | null, origin: QueueItem["origin"]): void {
    if (!list) return;
    add(
      Array.from(list).map((f) => ({ blob: f, filename: f.name, mime: f.type })),
      origin,
    );
  }

  /**
   * A record with no file at all — a policy number, an account reference.
   *
   * Added straight at `ready` with a nil blob and no transfer, rather than uploading
   * a zero-byte object: an empty file in the bucket would make `hasFile` true and put
   * an Open button on a document that opens nothing.
   */
  function addMetadataOnly(): void {
    const key = nextKey();
    details[key] = {
      title: "",
      docType: "generic",
      supplier: "",
      policyNumber: "",
      amount: "",
      documentDate: "",
      expiresAt: "",
      expiryKind: "",
      notifyOnExpiry: null,
      inCase: false,
      notes: "",
      dups: [],
      guessed: [],
      checks: [],
      filedId: null,
    };
    queue = [
      ...queue,
      {
        key,
        filename: "Record with no file",
        mime: "",
        sizeBytes: 0,
        blob: new Blob([]),
        origin: "picked",
        status: "ready",
        progress: 100,
        gcsPath: null,
        sha256: null,
        error: null,
        guess: { title: "", docType: "generic", documentDate: "", from: [] },
      },
    ];
  }

  function disconnectDrive(): void {
    drive.forget();
    driveConnected = false;
    driveFiles = [];
    drivePicked = {};
  }

  /**
   * The exact-duplicate check, once the hash exists.
   *
   * Runs on the sha256 alone here — supplier and amount are usually still blank at
   * this point, and the near-match needs both. The near-match runs again on blur of
   * those fields, which is when it can actually say anything.
   */
  async function lookForDuplicates(item: QueueItem): Promise<void> {
    if (!item.sha256) return;
    try {
      const r = await api.post<{ duplicates: Dup[] }>("/docs/duplicates", { sha256: item.sha256 });
      if (details[item.key]) details[item.key].dups = r.duplicates;
    } catch {
      // Never block filing on this. The document in someone's hand at a counter is
      // the thing that matters.
    }
  }

  async function checkNear(item: QueueItem): Promise<void> {
    const d = details[item.key];
    if (!d || (!d.supplier && !d.amount)) return;
    try {
      const r = await api.post<{ duplicates: Dup[] }>("/docs/duplicates", {
        sha256: item.sha256,
        supplier: d.supplier || null,
        amount: d.amount ? Number(d.amount) : null,
        documentDate: d.documentDate || null,
      });
      d.dups = r.duplicates;
    } catch {
      /* as above */
    }
  }

  // ── filing ───────────────────────────────────────────────────────────────

  async function file(item: QueueItem): Promise<void> {
    const d = details[item.key];
    if (!d) return;
    if (!d.title.trim()) {
      note = { kind: "warn", text: "Give it a title first." };
      return;
    }
    // A failed upload can still be filed as a metadata-only record — the spec says
    // metadata alone is a valid record — but silently dropping the file would be a
    // lie about what was kept.
    if (item.status === "uploading" || item.status === "hashing") {
      // Name the stage rather than say "give it a moment" regardless.
      // Every stage now times out and marks the row failed with a reason, so
      // this state is genuinely temporary — but saying WHICH stage is the
      // difference between waiting patiently and waiting forever without knowing
      // anything is wrong.
      note = {
        kind: "warn",
        text:
          item.status === "hashing"
            ? `Still reading ${item.filename} — it will say so here if it fails.`
            : `Still uploading ${item.filename} (${item.progress}%) — it will say so here if it fails.`,
      };
      return;
    }

    item.status = "filing";
    touch();
    try {
      const r = await api.post<{ id: string; checks: Check[] }>("/docs", {
        title: d.title,
        docType: d.docType,
        supplier: d.supplier || null,
        policyNumber: d.policyNumber || null,
        amount: d.amount ? Number(d.amount) : null,
        documentDate: d.documentDate || null,
        expiresAt: d.expiresAt || null,
        expiryKind: d.expiryKind || null,
        inCase: d.inCase,
        notifyOnExpiry: d.notifyOnExpiry,
        notes: d.notes || null,
        gcsPath: item.gcsPath,
        mime: item.gcsPath ? item.mime : null,
        sizeBytes: item.gcsPath ? item.sizeBytes : null,
        sha256: item.sha256,
      });
      d.checks = r.checks;
      d.filedId = r.id;
      item.status = "filed";
      note = {
        kind: r.checks.length > 0 ? "warn" : "ok",
        text:
          r.checks.length > 0
            ? `Filed “${d.title}”, with ${r.checks.length} thing${r.checks.length === 1 ? "" : "s"} worth checking.`
            : `Filed “${d.title}”.`,
      };
      onFiled();
    } catch (e) {
      item.status = "ready";
      note = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not file it." };
    } finally {
      touch();
    }
  }

  /**
   * File every row that is ready and titled.
   *
   * Sequential, not parallel: each file runs the new-supplier check and the duplicate
   * queries, and filing eight at once would have each of them miss the previous
   * seven — so eight copies of one bill would all report as the first of their kind.
   */
  async function fileAll(): Promise<void> {
    filingAll = true;
    try {
      for (const item of queue.filter((i) => i.status === "ready")) {
        if (details[item.key]?.title.trim()) await file(item);
      }
    } finally {
      filingAll = false;
    }
  }

  function remove(item: QueueItem): void {
    queue = queue.filter((i) => i.key !== item.key);
    delete details[item.key];
  }

  function clearFiled(): void {
    for (const i of queue.filter((x) => x.status === "filed")) delete details[i.key];
    queue = queue.filter((i) => i.status !== "filed");
  }

  async function retry(item: QueueItem): Promise<void> {
    item.error = null;
    item.status = "queued";
    touch();
    await transferAll([item], touch);
    void lookForDuplicates(item);
  }

  // ── the five ways in ─────────────────────────────────────────────────────

  function onDrop(e: DragEvent): void {
    e.preventDefault();
    dragging = false;
    const dt = e.dataTransfer;
    if (!dt) return;
    // `items` carries directory entries too, but a full directory walk needs the
    // non-standard webkitGetAsEntry tree. `files` already contains every file from a
    // dropped folder in every current browser, which covers the actual case.
    fromFileList(dt.files, "dropped");
  }

  /**
   * Ctrl+V. A screenshot or a photo copied from anywhere becomes a document.
   *
   * Bound on window, so it works without focusing a field first — the natural
   * gesture is to arrive on the page and paste.
   */
  function onPaste(e: ClipboardEvent): void {
    const items = Array.from(e.clipboardData?.items ?? []).filter((i) => i.kind === "file");
    if (items.length === 0) return;
    const files = items.map((i) => i.getAsFile()).filter((f): f is File => f !== null);
    if (files.length === 0) return;
    e.preventDefault();
    add(
      files.map((f, n) => ({
        blob: f,
        // A pasted screenshot is usually called "image.png", which infers nothing.
        // Stamping the date at least makes the row identifiable in a list of five.
        filename: f.name && f.name !== "image.png" ? f.name : `Pasted ${new Date().toISOString().slice(0, 10)}${files.length > 1 ? ` (${n + 1})` : ""}.png`,
        mime: f.type,
      })),
      "pasted",
    );
  }

  async function openDrive(): Promise<void> {
    driveOpen = true;
    driveErr = null;
    if (drive.isConnected()) {
      driveConnected = true;
      await loadDrive();
      return;
    }
    driveBusy = true;
    try {
      await drive.connect();
      driveConnected = true;
      await loadDrive();
    } catch (e) {
      driveErr = e instanceof drive.DriveError ? e.message : "Could not connect to Drive.";
      driveConnected = false;
    } finally {
      driveBusy = false;
    }
  }

  /**
   * Open Google's own file browser and add whatever comes back to the tray.
   *
   * This replaced listing the Drive ourselves. Under `drive.file` we cannot
   * enumerate anything we were not handed, and the old flat list was unusable
   * anyway — "newest 100 files in your Drive" with no folders is not somewhere a
   * person can find a title deed.
   *
   * Results accumulate rather than replace, so several trips into different
   * folders build one batch. Cancelling returns nothing and must not wipe what
   * is already staged.
   */
  async function loadDrive(): Promise<void> {
    driveBusy = true;
    driveErr = null;
    try {
      const picked = await drive.pick();
      if (picked.length === 0) return;
      const seen = new Set(driveFiles.map((f) => f.id));
      driveFiles = [...driveFiles, ...picked.filter((f) => !seen.has(f.id))];
      for (const f of picked) drivePicked[f.id] = true;
    } catch (e) {
      driveErr = e instanceof drive.DriveError ? e.message : "Could not open Drive.";
      if (!drive.isConnected()) driveConnected = false;
    } finally {
      driveBusy = false;
    }
  }

  /** Bring the ticked Drive files in. Downloads happen here; uploads start on add. */
  async function importDrive(): Promise<void> {
    const chosen = driveFiles.filter((f) => drivePicked[f.id]);
    if (chosen.length === 0) return;
    driveBusy = true;
    driveErr = null;
    const got: Array<{ blob: Blob; filename: string; mime: string }> = [];
    const failed: string[] = [];
    for (const f of chosen) {
      try {
        got.push(await drive.download(f));
      } catch {
        // Name what did not come through rather than reporting a clean import of
        // fewer files than were ticked.
        failed.push(f.name);
      }
    }
    driveBusy = false;
    add(got, "drive");
    drivePicked = {};
    driveOpen = false;
    if (failed.length > 0) {
      note = { kind: "warn", text: `Could not fetch ${failed.length} of ${chosen.length}: ${failed.join(", ")}.` };
    }
  }

  const ORIGIN_LABEL: Record<QueueItem["origin"], string> = {
    picked: "chosen",
    dropped: "dropped",
    pasted: "pasted",
    camera: "camera",
    drive: "Drive",
  };
</script>

<svelte:window
  onpaste={onPaste}
  ondragover={(e) => {
    e.preventDefault();
    dragging = true;
  }}
  ondragleave={(e) => {
    // Only when the pointer actually leaves the window: dragging across a child
    // element fires dragleave constantly and the zone would flicker.
    if (e.relatedTarget === null) dragging = false;
  }}
  ondrop={onDrop}
/>

<section class="card">
  <div class="sec">
    <span class="kicker">File a document</span>
    <span class="sub">drop, paste, photograph, or pull from Drive</span>
  </div>

  <!-- ── the ways in ────────────────────────────────────────────────────── -->
  <div class="zone" class:zone--hot={dragging}>
    <Icon name={dragging ? "plus" : "inbox"} size={22} />
    <p class="zone__lead">{dragging ? "Let go to add them" : "Drop files anywhere on this page"}</p>
    <p class="sub">or paste with Ctrl+V — uploading starts straight away</p>

    <div class="ways">
      <label class="way">
        <Icon name="file" size={14} />
        <span>Choose files</span>
        <input type="file" multiple onchange={(e) => { fromFileList(e.currentTarget.files, "picked"); e.currentTarget.value = ""; }} />
      </label>

      <!-- capture=environment opens the rear camera on a phone and is ignored on a
           desktop, so one control serves both without sniffing the user agent. -->
      <label class="way way--phone">
        <Icon name="camera" size={14} />
        <span>Photograph</span>
        <input type="file" accept="image/*" capture="environment" multiple
               onchange={(e) => { fromFileList(e.currentTarget.files, "camera"); e.currentTarget.value = ""; }} />
      </label>

      <button class="way" onclick={openDrive}>
        <Icon name="external" size={14} />
        <span>From Google Drive</span>
      </button>
    </div>
  </div>

  {#if note}
    <div class="attn" class:attn--warn={note.kind === "warn"}>
      <span class="attn__badge" aria-hidden="true"><Icon name={note.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{note.text}</span>
      <button class="lnk" onclick={() => (note = null)}>Dismiss</button>
    </div>
  {/if}

  <!-- ── Drive browser ──────────────────────────────────────────────────── -->
  {#if driveOpen}
    <div class="drive">
      <div class="drive__top">
        <strong>Google Drive</strong>
        <button class="lnk" onclick={() => (driveOpen = false)}>Close</button>
      </div>

      {#if driveErr}
        <p class="sub pad"><Icon name="alert" size={12} /> {driveErr}</p>
      {/if}

      {#if !driveConnected}
        <p class="sub pad">
          Vault asks Google for <strong>read-only</strong> access to your Drive, so it can copy a
          document in. It can never change or delete anything there. Google will warn that this app
          is unverified — that is expected for a household app; choose Advanced, then continue.
        </p>
        <div class="acts">
          <button class="btn-primary" disabled={driveBusy} onclick={openDrive}>
            {driveBusy ? "Connecting…" : "Connect Drive"}
          </button>
        </div>
      {:else}
        <!-- The name search is gone: Google's Picker has folders, breadcrumbs,
             search, Recent, Starred and shared drives built in, and it is the
             only way a drive.file app can be given a file at all. Reproducing a
             worse version of it beside the real one would just be confusing. -->
        <div class="drivebar">
          <button class="btn-primary" onclick={() => void loadDrive()} disabled={driveBusy}>
            <Icon name="search" size={14} />
            {driveBusy ? "Opening…" : driveFiles.length ? "Choose more…" : "Browse Drive…"}
          </button>
          <span class="sub">Folders, search and shared drives — Vault only sees what you pick.</span>
        </div>

        {#if driveBusy}
          <p class="sub pad">Waiting for Drive…</p>
        {:else if driveFiles.length === 0}
          <p class="sub pad"><Icon name="pause" size={12} /> Nothing chosen yet.</p>
        {:else}
          <div class="dlist">
            {#each driveFiles as f (f.id)}
              <label class="drow">
                <input type="checkbox" bind:checked={drivePicked[f.id]} />
                <span class="drow__name">{f.name}</span>
                <span class="sub">
                  {f.native ? "Google file → PDF" : f.size !== null ? size(f.size) : "—"}
                </span>
              </label>
            {/each}
          </div>
          <div class="acts">
            <button class="btn-primary" disabled={driveBusy} onclick={importDrive}>
              Bring in {Object.values(drivePicked).filter(Boolean).length || ""} selected
            </button>
            <button class="lnk" onclick={disconnectDrive}>Disconnect</button>
          </div>
        {/if}
      {/if}
    </div>
  {/if}

  <!-- ── the queue ──────────────────────────────────────────────────────── -->
  {#if queue.length > 0}
    <div class="qhead">
      <span class="sub">
        {pending.length} waiting{queue.length !== pending.length ? ` · ${queue.length - pending.length} filed` : ""}
      </span>
      <span class="qhead__acts">
        {#if readyCount > 1}
          <button class="btn-primary btn-sm" disabled={filingAll} onclick={fileAll}>
            {filingAll ? "Filing…" : `File all ${readyCount}`}
          </button>
        {/if}
        {#if queue.length !== pending.length}
          <button class="lnk" onclick={clearFiled}>Clear filed</button>
        {/if}
      </span>
    </div>

    {#each queue as item (item.key)}
      {@const d = details[item.key]}
      <article class="row" class:row--done={item.status === "filed"} class:row--bad={item.status === "failed"}>
        <div class="row__top">
          <span class="row__name">{item.filename}</span>
          <span class="sub">{size(item.sizeBytes)} · {ORIGIN_LABEL[item.origin]}</span>
          <span class="row__state">
            {#if item.status === "hashing"}<span class="sub">Step 1 of 3 · reading the file…</span>
            {:else if item.status === "uploading"}<span class="sub">Step 2 of 3 · uploading {item.progress}%</span>
            {:else if item.status === "ready"}<span class="sub"><Icon name="check" size={12} /> Uploaded</span>
            {:else if item.status === "filing"}<span class="sub">Step 3 of 3 · filing…</span>
            {:else if item.status === "filed"}<span class="sub"><Icon name="check" size={12} /> Filed</span>
            {:else if item.status === "failed"}<span class="sub"><Icon name="alert" size={12} /> {item.error}</span>
            {:else}<span class="sub">Queued</span>{/if}
          </span>
          {#if item.status !== "filed"}
            <button class="lnk" onclick={() => remove(item)} aria-label="Remove {item.filename}">Remove</button>
          {/if}
        </div>

        {#if item.status === "hashing" || item.status === "uploading" || item.status === "filing"}
          <!-- Real bytes-sent progress while uploading; a moving indeterminate bar
               for the two stages that cannot report a percentage. A bar that does
               not move reads as a hang, and people cancel and retry — which is how
               the duplicates item 29 exists to clean up get created. Every stage is
               also time-boxed, so this can no longer run forever in silence. -->
          <div class="bar" class:bar--waiting={item.status !== "uploading"}>
            <div class="bar__fill" style="width:{item.status === "uploading" ? item.progress : 100}%"></div>
          </div>
        {/if}

        {#if item.status === "failed"}
          <div class="acts">
            <button class="btn-primary btn-sm" onclick={() => retry(item)}>Try again</button>
            <span class="sub">Or file it as a record with no file attached, below.</span>
          </div>
        {/if}

        {#if d && item.status !== "filed"}
          {#if d.dups.length > 0}
            <!-- Presented for judgement. Filing anyway keeps both; nothing is ever
                 auto-deleted. -->
            <div class="attn attn--warn">
              <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
              <span class="attn__text">
                Looks like {d.dups.length === 1 ? "one you have" : `${d.dups.length} you have`} already:
                {#each d.dups as dup (dup.id)}<strong>{dup.title}</strong> — {dup.why}{" "}{/each}
                Filing anyway keeps both.
              </span>
            </div>
          {/if}

          <div class="grid">
            <label class="wide">
              <span class="lb">
                What is it
                {#if d.guessed.includes("title")}<span class="sub">· guessed from the filename</span>{/if}
              </span>
              <input bind:value={d.title} placeholder="Municipal bill, August" />
            </label>
            <label>
              <span class="lb">
                Type{#if d.guessed.includes("type")}<span class="sub"> · guessed</span>{/if}
              </span>
              <!-- Suggest an expiry kind from the type, on change rather than in an
                   $effect: an effect watching every row would re-apply the suggestion
                   over a kind the person had just picked by hand. Only ever fills a
                   blank — a suggestion, never a default that files itself. -->
              <select
                bind:value={d.docType}
                onchange={() => {
                  const s = meta?.suggested?.[d.docType];
                  if (s && !d.expiryKind) d.expiryKind = s;
                }}
              >
                {#each meta?.docTypes ?? [] as t (t)}<option value={t}>{pretty(t)}</option>{/each}
              </select>
            </label>
            <label>
              <span class="lb">Supplier or issuer</span>
              <input bind:value={d.supplier} onblur={() => checkNear(item)} placeholder="City of Tshwane" />
            </label>
            <label>
              <!-- The number you get asked for on the phone. Searchable, so typing
                   it finds the policy and typing the policy finds it. -->
              <span class="lb">Policy number <span class="sub">(or account / membership)</span></span>
              <input bind:value={d.policyNumber} placeholder="e.g. 12345678" autocomplete="off" spellcheck="false" />
            </label>
            <label>
              <span class="lb">Amount (R)</span>
              <input bind:value={d.amount} onblur={() => checkNear(item)} type="number" step="0.01" placeholder="4182.16" />
            </label>
            <label>
              <span class="lb">
                Date on it{#if d.guessed.includes("date")}<span class="sub"> · guessed</span>{/if}
              </span>
              <input bind:value={d.documentDate} onblur={() => checkNear(item)} type="date" />
            </label>
            <label>
              <span class="lb">Expires <span class="sub">(blank if none)</span></span>
              <input bind:value={d.expiresAt} type="date" />
            </label>
            <label>
              <span class="lb">Expiry kind</span>
              <select bind:value={d.expiryKind} disabled={!d.expiresAt}>
                <option value="">—</option>
                {#each meta?.expiryKinds ?? [] as k (k)}<option value={k}>{pretty(k)}</option>{/each}
              </select>
            </label>
            <label class="wide">
              <span class="lb">Notes</span>
              <input bind:value={d.notes} placeholder="Anything you would want to read in a year" />
            </label>
            <label class="tog wide">
              <input type="checkbox" bind:checked={d.inCase} />
              <span>Put it in the <strong>in case</strong> folder — someone else may need this without me</span>
            </label>
            <!-- Tri-state under the hood: null means "use the type's default", so
                 a licence disc nudges and a statement stays quiet without anyone
                 having to decide. Touching the box makes the choice explicit and
                 it wins from then on. -->
            <label class="tog wide">
              <input
                type="checkbox"
                checked={d.notifyOnExpiry ?? RENEWABLE_DEFAULT.has(d.docType)}
                disabled={!d.expiresAt}
                onchange={(e) => (d.notifyOnExpiry = e.currentTarget.checked)}
              />
              <span>
                Remind me before this expires
                {#if !d.expiresAt}<span class="sub">— needs an expiry date</span>{/if}
              </span>
            </label>
          </div>

          <div class="acts">
            <button class="btn-primary" disabled={item.status === "filing"} onclick={() => file(item)}>
              {item.status === "filing" ? "Filing…" : "File it"}
            </button>
          </div>
        {/if}

        {#if d && d.checks.length > 0}
          <!-- Deterministic cross-checks, in words. Never a percentage. -->
          <div class="checks">
            <div class="sub colhead">Worth a look</div>
            {#each d.checks as c (c.says)}
              <div class="sub check"><Icon name="alert" size={12} /> {c.says}</div>
            {/each}
          </div>
        {/if}
      </article>
    {/each}
  {/if}

  <!-- Metadata-only filing stays reachable with no file at all: the spec says
       metadata alone is a valid record, and "the policy number is on the fridge" is a
       real thing to want to keep. -->
  {#if queue.length === 0}
    <p class="sub pad">
      Nothing waiting. You can also
      <button class="lnk" onclick={addMetadataOnly}>file a record with no file</button>
      — a policy number or an account reference is worth keeping on its own.
    </p>
  {/if}
</section>

<style>
  /* Only what this screen adds; card/grid/attn/acts/sub come from the shared sheet. */
  .zone {
    display: grid;
    justify-items: center;
    gap: 0.35rem;
    padding: 1.5rem 1rem;
    margin: 0 0 0.9rem;
    border: 1px dashed var(--line);
    border-radius: 12px;
    text-align: center;
    transition: border-color 120ms, background 120ms;
  }
  /* Blue, never green: --ok is the estate's "good" and the palette carries no green
     at all, because red-green is exactly the axis that does not read here. */
  .zone--hot {
    border-color: var(--ok);
    background: color-mix(in srgb, var(--ok) 8%, transparent);
  }
  .zone__lead {
    margin: 0;
    font-weight: 600;
  }
  .ways {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
    margin-top: 0.6rem;
  }
  .way {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: var(--panel);
    color: inherit;
    font: inherit;
    font-size: 0.85rem;
    cursor: pointer;
  }
  .way:hover {
    border-color: var(--ok);
  }
  .way input {
    display: none;
  }

  .qhead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--line);
  }
  .qhead__acts {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
  }
  .btn-sm {
    padding: 0.35rem 0.7rem;
    font-size: 0.82rem;
  }

  .row {
    padding: 0.8rem 0;
    border-bottom: 1px solid var(--line);
  }
  .row--done {
    opacity: 0.55;
  }
  .row--bad {
    /* Amber, not red. --error resolves to amber across the estate for the same
       colour-blindness reason, and the glyph carries the meaning regardless. */
    border-left: 2px solid var(--warn);
    padding-left: 0.6rem;
  }
  .row__top {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
  .row__name {
    font-weight: 600;
    word-break: break-all;
  }
  .row__state {
    margin-left: auto;
  }

  /* Indeterminate: the stage is working but cannot say how far along it is.
     Honest about not knowing, rather than a fake percentage that creeps to 90%
     and stops — which is the pattern people have learned to distrust. */
  .bar--waiting .bar__fill {
    width: 40% !important;
    animation: slide 1.1s ease-in-out infinite;
  }
  @keyframes slide {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(250%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .bar--waiting .bar__fill { animation: none; width: 100% !important; opacity: 0.5; }
  }
  .bar {
    height: 4px;
    margin: 0.5rem 0 0.2rem;
    border-radius: 999px;
    background: var(--line);
    overflow: hidden;
  }
  .bar__fill {
    height: 100%;
    background: var(--ok);
    transition: width 140ms linear;
  }

  .drive {
    margin: 0 0 0.9rem;
    padding: 0.8rem;
    border: 1px solid var(--line);
    border-radius: 12px;
  }
  .drive__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }
  /* Wraps rather than truncates: the explanatory line is the bit that tells
     someone Vault cannot see the rest of their Drive, which is worth the space. */
  .drivebar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 2px 0 10px;
  }
  .drivebar .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .dlist {
    max-height: 15rem;
    overflow-y: auto;
    margin: 0.4rem 0;
  }
  .drow {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.4rem 0.2rem;
    border-bottom: 1px solid var(--line);
    cursor: pointer;
  }
  .drow__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 560px) {
    /* On a phone the camera is the point, so it leads. */
    .way--phone {
      order: -1;
    }
  }
</style>
